// Admin API service - uses secure JWT cookie-based endpoints instead of direct database access
// This ensures admin operations use the service_role key securely on Vercel backend

const API_BASE = '/api/admin';

// Handle API responses
async function handleResponse(response) {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }
  return data;
}

// Secure JSON headers
const defaultHeaders = {
  'Content-Type': 'application/json'
};

// Login admin - sets httpOnly cookie securely
export async function login(username, password) {
  const response = await fetch(`${API_BASE}/auth?action=login`, {
    method: 'POST',
    headers: defaultHeaders,
    body: JSON.stringify({ username, password })
  });
  return handleResponse(response);
}

// Logout admin - clears httpOnly cookie securely
export async function logout() {
  const response = await fetch(`${API_BASE}/auth?action=logout`, {
    method: 'POST',
    headers: defaultHeaders
  });
  return handleResponse(response);
}

// Verify admin session via httpOnly cookie JWT signature check
export async function verifySession() {
  const response = await fetch(`${API_BASE}/auth?action=verify`, {
    method: 'POST',
    headers: defaultHeaders
  });
  return handleResponse(response);
}


// ============ PRODUCTS ============
export const adminProductsService = {
  async getAll() {
    const response = await fetch(`${API_BASE}/products`, {
      method: 'GET',
      headers: defaultHeaders
    });
    return handleResponse(response);
  },

  async create(productData) {
    const response = await fetch(`${API_BASE}/products`, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(productData)
    });
    return handleResponse(response);
  },

  async update(id, productData) {
    const response = await fetch(`${API_BASE}/products`, {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify({ id, ...productData })
    });
    return handleResponse(response);
  },

  async delete(id) {
    const response = await fetch(`${API_BASE}/products`, {
      method: 'DELETE',
      headers: defaultHeaders,
      body: JSON.stringify({ id })
    });
    return handleResponse(response);
  },

  async toggleActive(id, active) {
    return this.update(id, { active });
  }
};

// ============ ORDERS ============
export const adminOrdersService = {
  async getAll() {
    const response = await fetch(`${API_BASE}/orders`, {
      method: 'GET',
      headers: defaultHeaders
    });
    return handleResponse(response);
  },

  async updateStatus(id, payment_status) {
    const response = await fetch(`${API_BASE}/orders`, {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify({ id, payment_status })
    });
    return handleResponse(response);
  },

  async delete(id) {
    const response = await fetch(`${API_BASE}/orders`, {
      method: 'DELETE',
      headers: defaultHeaders,
      body: JSON.stringify({ id })
    });
    return handleResponse(response);
  }
};

// ============ CUSTOMERS ============
export const adminCustomersService = {
  async getAll() {
    const response = await fetch(`${API_BASE}/customers`, {
      method: 'GET',
      headers: defaultHeaders
    });
    return handleResponse(response);
  }
};

// ============ SETTINGS ============
export const adminSettingsService = {
  async get() {
    const response = await fetch(`${API_BASE}/settings`, {
      method: 'GET',
      headers: defaultHeaders
    });
    return handleResponse(response);
  },

  async update(settingsData) {
    const response = await fetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: defaultHeaders,
      body: JSON.stringify(settingsData)
    });
    return handleResponse(response);
  }
};

// ============ ACTIVITY LOGS ============
export const adminActivityLogsService = {
  async getAll() {
    const response = await fetch(`${API_BASE}/activity-logs`, {
      method: 'GET',
      headers: defaultHeaders
    });
    return handleResponse(response);
  },

  async log(action, details, username) {
    try {
      const response = await fetch(`${API_BASE}/activity-logs`, {
        method: 'POST',
        headers: defaultHeaders,
        body: JSON.stringify({
          action,
          details,
          user: username || 'admin'
        })
      });
      return handleResponse(response);
    } catch (err) {
      console.error('Failed to log activity securely:', err);
    }
  }
};
